import React, { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IParkingPaymentsListData, ParkingLotSessionPaymentsPaymentOption, useApi } from 'components/providers/ApiProvider'
import { useParkingPayments } from 'ParkingPayments/ParkingPaymentsProvider'
import Alert from '@material-ui/lab/Alert'
import ListNoRecords from 'Elements/ListNoRecords'
import TableContainer from '@material-ui/core/TableContainer'
import Table from '@material-ui/core/Table'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import TableCell from '@material-ui/core/TableCell'
import TableBody from '@material-ui/core/TableBody'
import { formatDateTimeFieldToISO, formatMoney, formatTimestamp } from 'utils'
import { useMessage } from 'Elements/Message'
import PaymentOptionChip from 'Elements/PaymentOptionChip'
import MobillyPaymentOptionChip from 'Elements/MobillyPaymentOptionChip'
import { theme } from 'components/providers/ThemeProvider'
import { makeStyles } from '@material-ui/core/styles'
import EditPlateNumberDialog, { EditPlateNumberDialogData } from 'ParkingPayments/partials/EditPlateNumberDialog'
import ListIconButton from 'Elements/ListIconButton'
import IconButton from '@material-ui/core/IconButton'
import IconEdit from '@material-ui/icons/Edit'
import IconInfo from '@material-ui/icons/InfoOutlined'
import IconRefund from '@material-ui/icons/Undo'
import Tooltip from '@material-ui/core/Tooltip'
import IconSearchInNewWindow from '@material-ui/icons/Pageview'
import queryString from 'query-string'
import { addHours, format, subHours } from 'date-fns'
import RefundPaymentDialog, { RefundPaymentDialogData } from 'ParkingPayments/partials/RefundPaymentDialog'
import ListLoadingIndicator from 'Elements/ListLoadingIndicator'
import ListSkeleton from 'Elements/ListSkeleton'
import ListTablePagination from 'Elements/ListTablePagination'
import { Money } from '@material-ui/icons'
import Grid from '@material-ui/core/Grid'

const useClasses = makeStyles(theme => ({
  fadeTableRow: {
    '& td': {
      color: theme.palette.grey['A200'],
    },
  },
}))

const PlateNumber = ({ value }: { value: string }) => {
  return <span style={{ fontFamily: '"Roboto Mono", monospace' }}>{value}</span>
}

type Props = {}
type Functions = {}
type AllProps = Props & Functions

const List: FunctionComponent<AllProps> = ({}) => {
  const { t } = useTranslation()
  const api = useApi()
  const { filter, loadList, listLoading, setListLoading, listPerPage, setLoadChangePerPageList, listPage, setLoadChangePageList } = useParkingPayments()
  const { fadeTableRow } = useClasses()
  const { showError } = useMessage()

  const [modify, setModify] = useState<boolean>(false)
  const [payments, setPayments] = useState<IParkingPaymentsListData[]>(null)
  const [totalPayments, setTotalPayments] = useState(-1)
  const [totalLoading, setTotalLoading] = useState<boolean>(false)

  const [openEditPlateNumberDialog, setOpenEditPlateNumberDialog] = useState(false)
  const [editPlateNumberDialogData, setEditPlateNumberDialogData] = useState<EditPlateNumberDialogData | null>(null)

  const [openRefundPaymentDialog, setOpenRefundPaymentDialog] = useState(false)
  const [refundPaymentDialogData, setRefundPaymentDialogData] = useState<RefundPaymentDialogData | null>(null)

  const load = () => {
    setListLoading(true)

    api.getParkingPaymentsListItems({
      zone_id: filter.zone || undefined,
      plate_number: filter.plateNumber.toUpperCase() || undefined,
      ...(filter.paidFrom && { paid_from: formatDateTimeFieldToISO(filter.paidFrom) }),
      ...(filter.paidTill && { paid_till: formatDateTimeFieldToISO(filter.paidTill) }),
      state: filter.state || undefined,
      limit: listPerPage,
      page: listPage,
    })
      .then(data => {
        setListLoading(false)

        setModify(!!data.modify)
        setPayments(data.payments)

        if (data.isLastPage) {
          setTotalPayments((listPage - 1) * listPerPage + data.payments.length)
        } else {
          setTotalPayments(-1)
        }
      })
      .catch(error => {
        setListLoading(false)

        showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
      })
  }

  useEffect(() => {
    load()
  }, [loadList])

  const handleSearchPeriod = (zoneId: number, from: string, till: string) => {
    const url = {
      url: location.origin + '/my-parking-lot/parking-lot',
      query: {
        zone: zoneId.toString(),
        dateFrom: format(subHours(new Date(from), 1), 'dd.MM.yyyy HH:mm'),
        dateTill: format(addHours(new Date(till), 1), 'dd.MM.yyyy HH:mm'),
      },
    }

    window.open(queryString.stringifyUrl(url, { sort: false }), '_blank')
  }

  const handleSearchPlateNumber = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, zoneId: number, plateNumber: string) => {
    if (event.altKey && plateNumber.length >= 4) {
      plateNumber = plateNumber.substr(1, plateNumber.length - 2)
    }

    const url = {
      url: location.origin + '/my-parking-lot/parking-lot',
      query: {
        zone: zoneId.toString(),
        plateNumber,
      },
    }

    window.open(queryString.stringifyUrl(url, { sort: false }), '_blank')
  }

  const handleEditPlateNumber = (payment: IParkingPaymentsListData) => {
    setEditPlateNumberDialogData({ id: payment.external_payment_id, plateNumber: payment.plate_number })
    setOpenEditPlateNumberDialog(true)
  }

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => {
    if (listLoading) {
      return
    }

    if (page == -1) {
      setListLoading(true)
      setTotalLoading(true)

      api.getParkingPaymentsListTotal({
        zone_id: filter.zone || undefined,
        plate_number: filter.plateNumber.toUpperCase() || undefined,
        ...(filter.paidFrom && { paid_from: formatDateTimeFieldToISO(filter.paidFrom) }),
        ...(filter.paidTill && { paid_till: formatDateTimeFieldToISO(filter.paidTill) }),
        state: filter.state || undefined,
      })
        .then(data => {
          setTotalLoading(false)
          setTotalPayments(data.totalPayments)
          setLoadChangePageList(Math.ceil(data.totalPayments / listPerPage))
        })
        .catch(error => {
          setListLoading(false)
          setTotalLoading(false)

          showError(`${t('alerts.couldNotLoadData')} ${t('alerts.tryLater')}`)
        })

      return
    }

    setLoadChangePageList(page + 1)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setLoadChangePerPageList(parseInt(event.target.value, 10))
  }

  const handleRefund = (payment: IParkingPaymentsListData) => {
    setRefundPaymentDialogData({ payment_id: payment.id })
    setOpenRefundPaymentDialog(true)
  }

  return (
    <>
      {!payments && (
        <ListSkeleton />
      )}

      {payments && listLoading && (
        <ListLoadingIndicator />
      )}

      {payments && !payments.length && (
        <ListNoRecords />
      )}

      {payments && Boolean(payments.length) && (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('zone')}</TableCell>
                  <TableCell>{t('paidAt')}</TableCell>
                  <TableCell>{t('amount')}</TableCell>
                  <TableCell />
                  <TableCell>{t('period')}</TableCell>
                  <TableCell style={{ whiteSpace: 'nowrap' }}>{t('plateNumber')}</TableCell>
                  <TableCell>{t('session')}</TableCell>
                  {modify && <TableCell colSpan={2} />}
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment, key) => {
                  const plateNumber = payment.plate_number || (payment.parkingSessions.length ? payment.parkingSessions[0].plate_number : '?')
                  const hasUnfinishedSession = payment.parkingSessions.some(session => !session.exit_at)
                  const isPeriodOutsideSession = !!(payment.entry_at && payment.exit_at && payment.parkingSessions.some(session => (new Date(payment.exit_at).getTime() <= new Date(session.entry_at).getTime()) || (session.exit_at && new Date(payment.entry_at).getTime() >= new Date(session.exit_at).getTime())))
                  const fadeRow = !!payment.parkingSessions.length && !hasUnfinishedSession && !isPeriodOutsideSession

                  return (<TableRow key={key} hover {...(fadeRow && { className: fadeTableRow })}>
                    <TableCell>{payment.zone}</TableCell>
                    <TableCell>{formatTimestamp(payment.paid_at)}</TableCell>
                    <TableCell>
                      {formatMoney(payment.amount)}
                      {Boolean(payment.amount_refunded) && <> (<span style={{ color: '#ff9800' }}>{formatMoney(payment.amount_refunded)}</span>)</>}
                    </TableCell>
                    <TableCell style={{ paddingTop: 14, paddingBottom: 14 }}>
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyAutomatic && (
                        <MobillyPaymentOptionChip
                          letter="A"
                          label="Automatic"
                        />
                      )}
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyStartStop && (
                        <MobillyPaymentOptionChip
                          label="Start-stop"
                        />
                      )}
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyMPay && (
                        <MobillyPaymentOptionChip
                          label="Mpay"
                        />
                      )}
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyPostpay && (
                        <MobillyPaymentOptionChip
                          label="Postpay"
                        />
                      )}
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.CashRegister && (
                        <PaymentOptionChip
                          label={t('cashRegister')}
                        />
                      )}
                      {payment.option == ParkingLotSessionPaymentsPaymentOption.CashDesk && (
                        <PaymentOptionChip
                          icon={<Money style={{ color: 'rgba(0, 0, 0, 0.38)' }} />}
                          label={t('cashDesk')}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {(!payment.entry_at || !payment.exit_at) ? <>{`—`}</> : <>
                        <ListIconButton
                          icon={<IconSearchInNewWindow />}
                          color="secondary"
                          spacingLeft
                          onClick={() => handleSearchPeriod(payment.zone_id, payment.entry_at, payment.exit_at)}
                          style={{ marginRight: 12 }}
                        />
                        <span style={{
                          ...(isPeriodOutsideSession && { color: theme.palette.primary.main }),
                        }}>{formatTimestamp(payment.entry_at)} - {formatTimestamp(payment.exit_at)}</span>
                      </>}
                    </TableCell>
                    <TableCell>
                      <ListIconButton
                        icon={<IconSearchInNewWindow />}
                        color="secondary"
                        spacingLeft
                        onClick={(e) => handleSearchPlateNumber(e, payment.zone_id, plateNumber)}
                        style={{ marginRight: 12 }}
                      />
                      <PlateNumber value={plateNumber} />
                      {payment.modify && payment.external_payment_id && !payment.parkingSessions.length && (
                        <ListIconButton
                          icon={<IconEdit />}
                          spacingLeft
                          onClick={() => handleEditPlateNumber(payment)}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {!payment.parkingSessions.length ? <>{`—`}</> : payment.parkingSessions.map((session, key) => {
                        return <div key={key}>{formatTimestamp(session.entry_at)} - {session.exit_at ? formatTimestamp(session.exit_at) : '...'}</div>
                      })}
                    </TableCell>
                    {modify && (
                      <>
                        <TableCell align="center" style={{ width: 62 }}>
                          <Tooltip title={<>
                            {`Maksājums #: ${payment.id}`}
                          </>} placement={'top'} arrow interactive>
                            <IconInfo fontSize="small" style={{ verticalAlign: 'middle' }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ paddingLeft: 0, width: 46 }}>
                          {payment.modify && payment.external_payment_id && !payment.parkingSessions.length && payment.option == ParkingLotSessionPaymentsPaymentOption.MobillyStartStop && (
                            <IconButton
                              color="secondary"
                              size="small"
                              style={{ margin: '-5px 0' }}
                              onClick={() => handleRefund(payment)}
                            >
                              <IconRefund />
                            </IconButton>
                          )}
                        </TableCell>
                      </>
                    )}
                  </TableRow>)
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid container direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Alert variant="outlined" severity="warning" style={{ borderStyle: 'none' }}>
                {t('parkingLotPaymentsLimitInfo')}
              </Alert>
            </Grid>
            <Grid item style={{ marginLeft: 'auto' }}>
              <ListTablePagination
                count={totalPayments}
                rowsPerPage={listPerPage}
                page={listPage - 1}
                labelDisplayedRows={({ from, to }) => `${totalLoading ? '…' : from}-${listLoading ? '…' : to}`}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Grid>
        </>
      )}
      {modify && (
        <>
          <EditPlateNumberDialog open={openEditPlateNumberDialog} setOpen={setOpenEditPlateNumberDialog} data={editPlateNumberDialogData} />
          <RefundPaymentDialog open={openRefundPaymentDialog} setOpen={setOpenRefundPaymentDialog} data={refundPaymentDialogData} />
        </>
      )}
    </>
  )
}

export default List
